import { useMutation, useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../utils/axiosInstance";
import { message } from "antd";
import { queryClient } from "services/queryClient";
import { OrdersResponseType } from "Pages/User/OrderManagement/orders.type";
import { cleanObject } from "utils/cleanObject";
import { ApiArrayResponseType, ApiResponseType } from "types/api.types";
import { DeliveryType } from "types/deliveryType.enum";

export type OrderManagementQueryParams = {
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  source?: string;
  paymentStatus?: string;
  deliveryMethod?: string;
  page?: number;
};

export const useOrders = (params: OrderManagementQueryParams = {}) => {
  return useQuery({
    queryKey: ["useGetOrdersQuery", JSON.stringify(params)],
    queryFn: () =>
      axiosInstance
        .get<OrdersResponseType>("orders", {
          params: cleanObject(params),
        })
        .then((res) => res.data.data),
    // onError: (error: any) => {
    //   message.error(
    //     error?.response?.data?.message ||
    //       "An error occurred while fetching orders."
    //   );
    // },
  });
};

type OrderType = "bulk" | "unit";
type CreateManualOrderPayload = {
  type: OrderType;
  customerId: string;
  customerName: string;
  customerAddress?: string;
  customerPhone?: string;
  // deliveryMethod: string;
  paymentMethod: string;
  branch: string;
  orderProducts: OrderProduct[];
};

type OrderProduct = {
  productId: string;
  quantity: number;
  amount?: number;
};

type CreateOrderQuotePayload = {
  products: OrderProduct[];
  type: OrderType;
};
type CreateOrderQuoteResponse = {
  type: string;
  totalAmount: number;
  items: CreateOrderQuoteItem[];
};

type CreateOrderQuoteItem = {
  productId: string;
  productName: string;
  quantity: number;
  pricePerUnit: number;
  totalPrice: number;
  totalPriceAfterDiscount: number;
  discountAmount: number;
};

export type CreateManualOrderResponse = {
  orderNo: string;
  customer: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  deliveryMethod: string;
  orderStatus: string;
  source: string;
  paymentStatus: string;
  paymentMethod: string;
  notes: any[];
  totalProductsAmount: number;
  discountAmount: number;
  orderProducts: CreateManualOrderResponseProduct[];
  branch: Branch;
  productOrderType: string;
  isDeleted: boolean;
  statusLogs: any[];
  reference: string;
  createdAt: Date;
  updatedAt: Date;
  id: string;
};

type Branch = {
  name: string;
  lga: string;
  address: string;
  state: string;
  phoneNumber: string;
  countryCode: string;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
  codeNumber: number;
  code: string;
  id: string;
};

type CreateManualOrderResponseProduct = {
  orderId: string;
  customer: string;
  productName: string;
  image: string;
  unit: number;
  price: number;
  totalAmount: number;
  type: string;
  createdAt: Date;
  updatedAt: Date;
  id: string;
};

export const useCreateOrder = () => {
  const { mutate: createOrderMutation, isPending: createOrderLoading } =
    useMutation({
      mutationFn: (payload: CreateManualOrderPayload) =>
        axiosInstance
          .post("orders/manual", payload)
          .then((res) => res.data.data as CreateManualOrderResponse),
    });

  const {
    mutate: createOrderQuoteMutation,
    mutateAsync: createOrderQuoteMutationAsync,
    isPending: createOrderQuoteLoading,
  } = useMutation({
    mutationFn: (payload: CreateOrderQuotePayload) =>
      axiosInstance
        .post("orders/quote", payload)
        .then((res) => res.data.data as CreateOrderQuoteResponse),
  });

  return {
    createOrderQuoteMutation,
    createOrderQuoteLoading,
    createOrderMutation,
    createOrderLoading,
    createOrderQuoteMutationAsync,
  };
};

export interface OrderDetails {
  orderNo: string;
  customer: Customer;
  customerName: string;
  customerId: string;
  customerAddress: string;
  customerPhone: string;
  customerEmail: string;
  deliveryMethod: DeliveryType;
  orderStatus: string;
  source: string;
  paymentStatus: string;
  paymentMethod: string;
  notes: any[];
  totalProductsAmount: number;
  discountAmount: number;
  orderProducts: OrderDetailsProduct[];
  productOrderType: string;
  deliveryDriver: {
    email: string;
    firstName: string;
    lastName: string;
    id: string;
  };
  measurement: string;
  isDeleted: boolean;
  statusLogs: StatusLog[];
  reference: string;
  createdAt: string;
  updatedAt: string;
  id: string;
}

type StatusLog = {
  status: string;
  admin?: StatusLogAdmin;
  note: string;
  createdAt: string;
  updatedAt: string;
  id: string;
};

type StatusLogAdmin = {
  email: string;
  firstName: string;
  lastName: string;
  id: string;
};

interface Customer {
  firstName: string;
  middleName: string;
  lastName: string;
  businessName: string;
  id: string;
}

export interface OrderDetailsProduct {
  productName: string;
  image: string;
  unit: number;
  price: number;
  totalAmount: number;
  id: string;
  measurement: string;
}

export type OrderDrivers = {
  email: string;
  firstName: string;
  lastName: string;
  role: DriverRole;
  passwordChangedAt: any;
  isSuspended: boolean;
  isDeleted: boolean;
  gender: string;
  phoneNumber: string;
  phoneCountryCode: string;
  lastLogin: any;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  id: string;
};

type DriverRole = {
  name: string;
  description: string;
  key: string;
  colorCode: string;
  permission: any[];
  id: string;
};

export const useOrder = (orderId?: string) => {
  const queryKey = ["order", orderId];

  const queryData = useQuery({
    queryKey,
    queryFn: () =>
      axiosInstance
        .get<ApiResponseType<OrderDetails>>(`orders/${orderId}`)
        .then((res) => res.data),
    enabled: !!orderId,
  });

  const { mutate: acceptOrderMutation, isPending: acceptOrderLoading } =
    useMutation({
      mutationFn: () =>
        axiosInstance.patch(`orders/${orderId}/accept`).then((res) => res.data),
      onSuccess: async (data: any) => {
        queryClient.invalidateQueries({ queryKey: ["useGetOrdersQuery"] });
        message.success(data?.message);
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message || "Failed to accept order"
        );
      },
    });

  const { mutate: cancelOrderMutation, isPending: cancelOrderLoading } =
    useMutation({
      mutationFn: (payload: { reason: string }) =>
        axiosInstance
          .patch(`orders/${orderId}/cancel`, {
            data: payload,
          })
          .then((res) => res.data),
      onSuccess: async (data: any) => {
        queryClient.invalidateQueries({ queryKey: ["useGetOrdersQuery"] });
        message.success(data?.message);
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message || "Failed to cancel order"
        );
      },
    });

  const { mutate: updateOrderMutation, isPending: updateOrderLoading } =
    useMutation({
      mutationFn: (payload: {
        deliveryMethod?: string;
        paymentMethod?: string;
        notes?: string;
      }) =>
        axiosInstance
          .patch(`orders/${orderId}/update`, payload)
          .then((res) => res.data),
      onSuccess: async (data: any) => {
        queryClient.invalidateQueries({ queryKey: ["useGetOrdersQuery"] });
        queryData.refetch();
        message.success(data?.message);
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message || "Failed to update order"
        );
      },
    });

  const { mutate: assignDriver, isPending: assignDriverLoading } = useMutation({
    mutationFn: (payload: { driverId: string }) =>
      axiosInstance
        .patch(`orders/${orderId}/drivers/${payload.driverId}`, payload)
        .then((res) => res.data),
    onSuccess: async (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["useGetOrdersQuery"] });
      queryData.refetch();
      message.success(data?.message);
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.message || "Failed to assign driver"
      );
    },
  });

  const { mutate: markOrderAsShipped, isPending: markOrderAsShippedLoading } =
    useMutation({
      mutationFn: (payload: { note?: string }) =>
        axiosInstance
          .patch(`orders/${orderId}/mark-as-shipped`, payload)
          .then((res) => res.data),
      onSuccess: async (data: any) => {
        queryClient.invalidateQueries({ queryKey: ["useGetOrdersQuery"] });
        queryData.refetch();
        message.success(data?.message);
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message || "Failed to mark as shipped"
        );
      },
    });

  return {
    ...queryData,
    acceptOrderMutation,
    acceptOrderLoading,
    cancelOrderMutation,
    cancelOrderLoading,
    updateOrderMutation,
    updateOrderLoading,
    assignDriver,
    assignDriverLoading,
    markOrderAsShipped,
    markOrderAsShippedLoading,
  };
};

export const useOrderDrivers = ({ search = "" }: { search?: string } = {}) => {
  const queryKey = ["drivers", search];

  const queryData = useQuery({
    queryKey,
    queryFn: () =>
      axiosInstance
        .get<ApiArrayResponseType<OrderDrivers>>(`orders/drivers`)
        .then((res) => res.data),
  });

  return {
    ...queryData,
  };
};
